import { useCallback, useRef, useState } from 'react';

/**
 * useSubmitGuard — THE double-submit guard, extracted 2026-08-13 from
 * `components/OrderForm.js`'s hand-rolled ref guard (the fix for the live
 * report *"if you hit submit twice on the order page it can make a duplicate
 * order"*). One implementation, applied per button — never a global dedupe in
 * `utils/api.js`, which would change semantics for legitimately-repeated POSTs
 * (poke, take/release).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A REF *AND* A STATE, AND WHY THE REF IS THE GATE
 * ─────────────────────────────────────────────────────────────────────────────
 * The defect class this closes is the FAST double-click: two clicks in one
 * frame, before any re-render. A `useState` flag set before an `await` cannot
 * close it — state is asynchronous, so the second click still reads the old
 * value. (That "partial guard" shape — `setLoading(true)` then `await` — was
 * the norm on ~30 surfaces here and is exactly the hole.) The ref is written
 * SYNCHRONOUSLY before any awaitable work, so the second call early-returns in
 * the same tick. `busy` is the render-triggering mirror, read ONLY by
 * `disabled=`/spinner UI — never gate logic on it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 *   const { guard, busy } = useSubmitGuard();
 *   <button disabled={busy} onClick={() => guard(() => doTheWork())}>
 *
 * `guard(fn)`:
 *  - while a previous guarded call is in flight → returns `undefined` WITHOUT
 *    calling `fn` (the double-click is dropped, not queued);
 *  - otherwise arms the gate, calls `fn()`, and returns `fn`'s return value;
 *  - 🪤 thenable-aware, NOT `async/await`, on purpose: a SYNCHRONOUS `fn`
 *    releases before `guard` returns (sequential submits all land — the exact
 *    behaviour OrderForm's callers relied on), while a promise-returning `fn`
 *    holds the gate until the promise SETTLES — resolve or reject — so a
 *    failed save is retryable and a slow one is not double-firable;
 *  - a synchronous THROW releases and rethrows (nothing was started, so
 *    nothing should stay armed).
 *
 * ⚠️ Scope: CLIENT-side only — one component instance, one tab. Two tabs, or a
 * network-layer retry, need the server-side idempotency signals
 * (`Idempotency-Key`, `expected_step_order`) this guard's call sites send.
 *
 * ⚠️ One hook instance = one gate. A page that guards several INDEPENDENT
 * workflows with one instance makes them mutually exclusive while in flight;
 * that is usually right for row actions on one list (the list is about to
 * refetch under you) but call it once per workflow where dropping a concurrent
 * legitimate click would lose user input.
 *
 * `setBusy(false)` after unmount is a React 18 no-op — the inline editor's
 * success path collapses the row before the promise settles, and that is fine.
 */
export default function useSubmitGuard() {
  const armedRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const guard = useCallback((fn) => {
    if (armedRef.current) return undefined;
    armedRef.current = true;
    setBusy(true);
    const release = () => {
      armedRef.current = false;
      setBusy(false);
    };
    let result;
    try {
      result = fn();
    } catch (err) {
      release();
      throw err;
    }
    if (result && typeof result.then === 'function') {
      result.then(release, release);
    } else {
      release();
    }
    return result;
  }, []);

  return { guard, busy };
}
